import React, { useState, useEffect, useLayoutEffect } from 'react';
import cx from 'classnames';
import DateInputProps, { DisplayFormats } from './DateInput.types';
import styles from './DateInput.module.scss';
import Select from '../../atoms/Select';

import { daysInMonth, yearsArray, makeDaysArray, getDateFromString, getDisplayFormat } from '../../utils/date';

const dayProps = {
  initialiseAsNull: true,
  graphical: true,
  nullText: 'DD',
  controlled: true,
};

const monthProps = {
  initialiseAsNull: true,
  graphical: true,
  nullText: 'MM',
  controlled: true,
};

const yearProps = {
  initialiseAsNull: true,
  graphical: true,
  nullText: 'YYYY',
  controlled: true,
};

const monthsArray = [
  { title: '01', const: '01' },
  { title: '02', const: '02' },
  { title: '03', const: '03' },
  { title: '04', const: '04' },
  { title: '05', const: '05' },
  { title: '06', const: '06' },
  { title: '07', const: '07' },
  { title: '08', const: '08' },
  { title: '09', const: '09' },
  { title: '10', const: '10' },
  { title: '11', const: '11' },
  { title: '12', const: '12' },
];

const pad = (date: number) => {
  if (Number.isNaN(date)) {
    return date;
  }
  return date.toString().padStart(2, '0');
};

const daysInitialValue = makeDaysArray(31).reverse();

const DateInput = ({
  value,
  displayFormat = DisplayFormats.dmy,
  onChange = () => {},
  dataPointer,
  dataCy = 'dateInput',
  invalid,
  errorMsgHolderId,
  locale,
}: DateInputProps) => {
  const [daysArray, setDaysArray] = useState<Array<{ title: string; const: string }>>(daysInitialValue);
  const date = new Date(value);
  const dayValue = `${pad(date.getDate()) || dayProps.nullText}`;
  const monthValue = `${pad(date.getMonth() + 1) || monthProps.nullText}`;
  const yearValue = `${date.getFullYear() || yearProps.nullText}`;
  const [currentMonth, setCurrentMonth] = useState<string>(monthValue);
  const [currentYear, setCurrentYear] = useState<string>(yearValue);
  const [currentDay, setCurrentDay] = useState<string>(dayValue);
  const [displayFormatState, setDisplayFormatState] = useState(displayFormat);

  useLayoutEffect(() => {
    if (!locale) return;
    const formatString = getDisplayFormat(locale);
    setDisplayFormatState(DisplayFormats[formatString]);
  }, []);

  const resetDay = () => setCurrentDay(dayValue);

  useEffect(() => {
    const shouldReset = !daysArray.filter((day) => day.title === currentDay).length;

    if (currentMonth && currentYear && shouldReset) {
      resetDay();
    }
  }, [daysArray, currentMonth, currentYear]);

  useEffect(() => {
    const date = getDateFromString(currentDay, currentMonth, currentYear);

    const fakeEvent = {
      currentTarget: {
        value: date ? date.toISOString() : null,
        getAttribute: (value: string) => {
          if (value === 'type') return 'change';
          if (value === 'data-pointer') return dataPointer;
          return null;
        },
      },
    } as React.ChangeEvent<HTMLSelectElement>;

    onChange(fakeEvent);
  }, [currentYear, currentDay, currentMonth]);

  const setDays = (month: string, year: string) => {
    if (!month || !year) return;
    const days = daysInMonth(Number(month), Number(year));
    const daysArray = makeDaysArray(days).reverse();

    setDaysArray(daysArray.length === 0 ? daysInitialValue : daysArray);
  };

  const handleDay = (e: React.FormEvent<HTMLSelectElement>) => {
    const target = e.target as HTMLSelectElement;
    const day = target.value;
    setCurrentDay(day);
    setDays(currentMonth, currentYear);
  };
  const handleMonth = (e: React.FormEvent<HTMLSelectElement>) => {
    const target = e.target as HTMLSelectElement;
    const month = target.value;
    setCurrentMonth(month);
    setDays(month, currentYear);
  };
  const handleYear = (e: React.FormEvent<HTMLSelectElement>) => {
    const target = e.target as HTMLSelectElement;
    const year = target.value;
    setCurrentYear(year);
    setDays(currentMonth, year);
  };

  const daysSelect = (
    <Select
      dataCy={`${dataCy}Days`}
      options={daysArray}
      {...dayProps}
      className={cx(styles.select, styles.day)}
      onChange={handleDay}
      controlledText={currentDay || dayProps.nullText}
      invalid={invalid}
    />
  );

  const monthsSelect = (
    <Select
      dataCy={`${dataCy}Months`}
      options={monthsArray}
      {...monthProps}
      className={cx(styles.select, styles.month)}
      onChange={handleMonth}
      controlledText={currentMonth || monthProps.nullText}
      invalid={invalid}
    />
  );

  const firstDateValue = () => {
    if (displayFormatState === DisplayFormats.dmy) return daysSelect;
    return monthsSelect;
  };

  const secondDateValue = () => {
    if (displayFormatState === DisplayFormats.dmy) return monthsSelect;
    return daysSelect;
  };

  return (
    <div className={styles.dateSelect} data-pointer={dataPointer} data-cy={dataCy}>
      <div className={cx(styles.datesWrapper)} aria-errormessage={errorMsgHolderId}>
        {firstDateValue()}
        {secondDateValue()}
        <Select
          dataCy={`${dataCy}Years`}
          options={yearsArray}
          {...yearProps}
          className={cx(styles.select, styles.year)}
          onChange={handleYear}
          controlledText={currentYear || yearProps.nullText}
          invalid={invalid}
        />
      </div>
    </div>
  );
};

export default DateInput;
